export default class BlueconicConnector{
  /**
	 * The Blueconic connector wraps the blueconic client. It adds some helper
	 * methods for CXC and simplifies some operations by wrapping the
	 * callback-based API with Promises.
	 *
	 * @param {object} client, the Blueconic Client object to use.
	 */
  constructor (client) {
    this.client = client;
  }

  /**
	 * Fetches the current BlueConic profile object.
	 *
	 * @returns {object|null} the current Blueconic profile object upon success,
	 * null upon failure.
	 */
  getProfile () {
    try {
      return this.client.profile.getProfile();
    } catch (e) {
      return null;
    }
  }

  /**
	 * Makes the Blueconic Client request the latest values for the properties
	 * provided. The retrieved properties can then be retrieved from the profile
	 * as usual.
	 *
	 * @param {properties[string]}, array of keys for which to retrieve values;
	 * @returns Promise that resolves when the properties have been retrieved.
	 */
  loadValues (properties) {
    return new Promise(resolve => {
      const profile = this.getProfile();
      profile.loadValues(properties, this, resolve);
    }).catch(() => null);
  }

  /**
	 * Makes the Blueconic Client request a complete list of property values.
	 *
	 * @returns a promise that resolves with an array of property key strings.
	 */
  getProfileProperties () {
    return new Promise((resolve) => {
      this.client.getProfileProperties(this, properties => {
        resolve(properties);
      });
    }).catch(() => null);
  }

  /**
	 * Sets the values for the properties contained in the value map provided.
	 *
	 * @param {object} obj, an object containing value strings or string arrays.
	 */
  setProfileData (obj) {
    try {
      const profile = this.getProfile();
      if (profile) {
        let key, value;
        for ([key, value] of Object.entries(obj)) {
          if (typeof value === 'string') {
            profile.setValue(key, value);
          } else {
            profile.setValues(key, value);
          }
        }
      }
    } catch (e) {
      return null;
    }
  }

  /**
	 * Tells the Blueconic client to synchronize profile data with the server.
	 *
	 * @returns a promise that resolves upon successful synchronization.
	 */
  updateProfile () {
    return new Promise(resolve => {
      this.client.profile.updateProfile(this, resolve);
    }).catch(() => null);
  }

  /**
	 * Utility method;
	 *
	 * @returns the BlueConic profile values needed for CX API calls
	 */
  cxGetProfileValues () {
    const out = {};

    try {
      // Make sure we have access to the getSegments method
      if (typeof this.client.getSegments === 'function') {

        const segments = this.client.getSegments();
        if (segments.length > 0) {
          for (let si = segments.length - 1; si >= 0; si--) {
            // Segments names are formatted as '[CXC] 01' in BlueConic
            if (segments[si].name.indexOf('[CXC] ') === 0) {
              // CXC Accepts the name, in format 'CXC_01'
              out.blueconic_segment = segments[si].name.replace('[','').replace(']','').replace(' ','_');
              break;
            }
          }
        }
      }

      const profile = this.getProfile();
      if (profile) {

        const bcLoggedin = profile.getValue('login_state'); // yes|no
        out['dim.blueconic_login_state'] = (bcLoggedin === 'yes' ? 'loggedin':'not_loggedin');

        const bcHasSapBpId = profile.getValue('hasSAPBPID'); // true|undefined
        if (bcHasSapBpId === 'true') {
          out.blueconic_hasSAPBPID = 'True';
        }
      }
    } catch (e) {
      /**/
    }

    return out;
  }
}
